import { useRouter } from 'next/router';
import { memo, useMemo } from 'react';

import { type Board, type Clip } from '@air/api/types';

import { PublicShowcaseReduxProvider } from '~/components/PublicShowcaseReduxProvider';
import { PublicGalleryAsset } from '~/components/v2/components/board/gallery/PublicGalleryAsset';
import { PublicGalleryBoard } from '~/components/v2/components/board/gallery/PublicGalleryBoard';
import { PublicGalleryFile } from '~/components/v2/components/board/gallery/PublicGalleryFile';
import { type GalleryViewProps } from '~/components/v2/components/shared/gallery/GalleryView';
import { PublicBoardsAndAssetsSelectionBar } from '~/components/v2/components/shared/selectionBar/PublicBoardsAndAssetsSelectionBar';
import { usePublicUrlShowcaseBoardHref } from '~/components/v2/components/showcase/hooks/usePublicUrlShowcaseBoardHref';
import { usePublicUrlShowcasePageNavigation } from '~/components/v2/components/showcase/hooks/usePublicUrlShowcasePageNavigation';
import { PUBLIC_SHOWCASE_LAYOUT_DATA } from '~/components/v2/components/showcase/publicShowcaseLayoutData';
import { renderPublicShowcaseBlock } from '~/components/v2/components/showcase/renderPublicShowcaseBlock';
import { useUI2026Styles } from '~/components/v2/hooks/useUI2026Styles';
import { PublicShowcaseLayout, type PublicShowcaseLayoutProps } from '~/components/v2/layouts/PublicShowcaseLayout';
import { createShortUrlRoute } from '~/constants/routes';
import { usePublicUrlBoardNavigation } from '~/hooks/usePublicUrlBoardNavigation';
import { PublicUrlAssetModalNavigationProvider } from '~/providers/PublicUrlAssetModalNavigationProvider';
import { getBoardIdFromPath } from '~/utils/PathUtils';

export type PublicShowcaseViewProps = Pick<
  PublicShowcaseLayoutProps,
  | 'collection'
  | 'permissions'
  | 'presentationSettings'
  | 'presentationContent'
  | 'presentationPages'
  | 'requireAccount'
  | 'isOnPaidPlan'
  | 'shortId'
>;

export type PublicShowcaseViewContentProps = PublicShowcaseViewProps;

// The public asset/file cards, supplied to the layout's composition contract. They close over
// nothing, so they live once at module scope.
const renderAsset: GalleryViewProps<Board, Clip>['renderAsset'] = ({ box, data, index }) => (
  <PublicGalleryAsset box={box} data={data} index={index} key={data.id} />
);

const renderFile: GalleryViewProps<Board, Clip>['renderFile'] = ({ box, data, index }) => (
  <PublicGalleryFile box={box} data={data} index={index} key={data.id} />
);

// Viewers render the cover bare — the authoring chrome around it is the editor canvas's seam.
const renderCover: PublicShowcaseLayoutProps['renderCover'] = ({ children }) => children;

// The body below redux: the board-drill binding (usePublicUrlBoardNavigation) seeds the centralized
// board into the collection tree's OWN store, so the component that builds it must render below
// PublicShowcaseReduxProvider — which the outer shell mounts.
const PublicShowcaseViewContent = memo(
  ({
    collection,
    permissions,
    presentationContent,
    presentationPages,
    presentationSettings,
    requireAccount,
    isOnPaidPlan,
    shortId,
  }: PublicShowcaseViewContentProps) => {
    const basePath = createShortUrlRoute(shortId);

    // URL-backed page navigation on the live route base, supplied to the layout's composition
    // contract. PublicUrlAssetModalNavigationProvider reads the same hook itself for board-drill trails.
    const { activePage, getPageNavigationHref, onSelectPage } = usePublicUrlShowcasePageNavigation({
      basePath,
      presentationPages,
    });

    // Board-entry hrefs on the same live route base, supplied to the layout's composition contract.
    const { getBoardNavigationHref } = usePublicUrlShowcaseBoardHref({ basePath });

    // The collection tree's board-drill action, supplied to the layout's composition contract —
    // pushes the board route with the active page's `?page=` trail and seeds the centralized board.
    const { onNavigateToBoard } = usePublicUrlBoardNavigation({ shortId, basePath, activePage });

    // The public board card, closed over this collection tree's own drill — the card renderer and
    // the drill travel together so a listed board's click always lands on this surface's routes.
    const renderBoard = useMemo<GalleryViewProps<Board, Clip>['renderBoard']>(
      () =>
        ({ box, data, index }) => (
          <PublicGalleryBoard box={box} data={data} index={index} key={data.id} onNavigateToBoard={onNavigateToBoard} />
        ),
      [onNavigateToBoard],
    );

    // The board the viewer is drilled into — the /a route's drill segment (/b/{boardId}).
    const { asPath } = useRouter();
    const activeBoardId = getBoardIdFromPath(asPath) ?? null;

    return (
      <PublicUrlAssetModalNavigationProvider>
        <PublicShowcaseLayout
          activeBoardId={activeBoardId}
          activePage={activePage}
          collection={collection}
          getBoardNavigationHref={getBoardNavigationHref}
          getPageNavigationHref={getPageNavigationHref}
          onNavigateToBoard={onNavigateToBoard}
          onSelectPage={onSelectPage}
          permissions={permissions}
          presentationSettings={presentationSettings}
          presentationContent={presentationContent}
          presentationPages={presentationPages}
          renderAsset={renderAsset}
          renderBlock={renderPublicShowcaseBlock}
          renderBoard={renderBoard}
          renderCover={renderCover}
          renderFile={renderFile}
          {...PUBLIC_SHOWCASE_LAYOUT_DATA}
          requireAccount={requireAccount}
          isOnPaidPlan={isOnPaidPlan}
          SelectionBar={PublicBoardsAndAssetsSelectionBar}
          shortId={shortId}
        />
      </PublicUrlAssetModalNavigationProvider>
    );
  },
);

PublicShowcaseViewContent.displayName = 'PublicShowcaseViewContent';

// A component (not JSX inline in the route branch) because the passcode-gated mount renders inside
// a render prop, where hooks can't be called — and the navigation bindings are hooks. Both
// collection mounts (gated and plain) render this ONE tree.
export const PublicShowcaseView = memo(
  ({
    collection,
    permissions,
    presentationContent,
    presentationPages,
    presentationSettings,
    requireAccount,
    isOnPaidPlan,
    shortId,
  }: PublicShowcaseViewProps) => {
    useUI2026Styles();

    return (
      // Redux above the navigation bindings: the drill's centralized-board seed dispatches into the
      // collection tree's store (see useSeedCentralizedPublicBoard), so the store must exist above
      // the content component that builds them.
      <PublicShowcaseReduxProvider>
        <PublicShowcaseViewContent
          collection={collection}
          permissions={permissions}
          presentationContent={presentationContent}
          presentationPages={presentationPages}
          presentationSettings={presentationSettings}
          requireAccount={requireAccount}
          isOnPaidPlan={isOnPaidPlan}
          shortId={shortId}
        />
      </PublicShowcaseReduxProvider>
    );
  },
);

PublicShowcaseView.displayName = 'PublicShowcaseView';
